import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { BookOpen, Users, Award, Target, FileCheck, Trophy } from 'lucide-react';
import { Card, CardContent } from '../ui/card';

const offerings = [
  {
    id: 1,
    icon: BookOpen,
    title: 'Exclusive Courses',
    description: 'Discover a wealth of exclusive ISO certification and professional development courses, innovatively designed for your success'
  },
  {
    id: 2,
    icon: Target,
    title: 'Methodical Learning',
    description: 'Meticulously designed courses tailored to provide you with the knowledge and skills needed to excel in your field'
  },
  {
    id: 3,
    icon: Users,
    title: 'Community & Networking',
    description: 'Interact and network with like-minded professionals from various backgrounds and industries'
  },
  {
    id: 4,
    icon: Award,
    title: 'Learn from Experts',
    description: 'Experience transformative learning through courses designed and delivered by industry-recognized experts'
  },
  {
    id: 5,
    icon: FileCheck,
    title: 'Practice & Assessment',
    description: 'Enhance your learning with practical exercises, case studies, and assessments to track your progress'
  },
  {
    id: 6,
    icon: Trophy,
    title: 'Get Certified',
    description: 'Earn recognized certifications that you can proudly showcase to advance your career'
  }
];

export const WhatWeOffer = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1 }
    }
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 50 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5, ease: [0.22, 1, 0.36, 1] }
    }
  };

  return (
    <section ref={ref} className="py-20 bg-gradient-to-br from-slate-50 to-blue-50">
      <div className="container mx-auto px-4">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : { opacity: 0, y: 30 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16 space-y-4"
        >
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900">
            What <span className="text-blue-600">We Offer</span>
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive training solutions designed to elevate your skills and accelerate your career growth
          </p>
        </motion.div>

        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={isInView ? "visible" : "hidden"}
          className="grid md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {offerings.map((offering) => {
            const Icon = offering.icon;
            return (
              <motion.div key={offering.id} variants={cardVariants}>
                <Card className="group h-full hover:shadow-2xl transition-all duration-300 border-2 border-transparent hover:border-blue-300 cursor-pointer bg-white">
                  <CardContent className="pt-8 pb-6 text-center">
                    <motion.div
                      className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6 group-hover:bg-blue-600 transition-all duration-300"
                      whileHover={{ scale: 1.1, rotate: 360 }}
                      transition={{ duration: 0.6 }}
                    >
                      <Icon className="h-10 w-10 text-blue-600 group-hover:text-white transition-colors duration-300" />
                    </motion.div>
                    <h3 className="text-xl font-bold text-gray-900 mb-3 group-hover:text-blue-600 transition-colors">
                      {offering.title}
                    </h3>
                    <p className="text-gray-600 leading-relaxed">
                      {offering.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};
